// Gestión del pago de UN día de un repartidor: marcar pagado/no pagado, agregar/quitar
// extras (bono/descuento) y retener/liberar paquetes con motivo. Cada acción persiste
// en billing y recarga el detalle; avisa al padre (onChanged) para refrescar el resumen.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../../../utils/toast';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Lock, Unlock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import {
  getDriverPayrollDay,
  markDriverDayPaid,
  setDriverPaymentDate,
  addDriverDayExtra,
  removeDriverDayExtra,
  withholdDriverDayPackage,
  releaseDriverDayPackage,
} from '../../../api/billing';
import { driverVehicleLabel } from '../../../utils/driverVehicleTypes';
import { billingDocLabel } from '../../../utils/billingDocTypes';
import { formatCurrency } from './utils/cobroHelpers';
import { dayLabel, todayYMD } from './utils/payrollWeeks';
import { extraAuditLabel, stampExtraDescription } from './utils/extraStamp';
import useAuth from '../../../hooks/useAuth';

const STATUS = {
  paid: { label: 'Pagado', cls: 'success' },
  partial: { label: 'Pago parcial', cls: 'warning' },
  unpaid: { label: 'No pagado', cls: 'neutral' },
};

// Hora de un retiro en la zona horaria del modal: es la misma `tz` con la que billing
// asignó el retiro a este día, así la hora siempre cae dentro del día que se gestiona.
// Una tz inválida cae a la hora local (Intl lanza RangeError); un instante que no parsea
// devuelve null y no se muestra.
const timeLabel = (iso, tz) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const opts = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
  try {
    return new Intl.DateTimeFormat('es-CL', tz ? { ...opts, timeZone: tz } : opts).format(d);
  } catch {
    return new Intl.DateTimeFormat('es-CL', opts).format(d);
  }
};

// Solo se enlaza el PDF del remito si la URL es http(s): viene de otro servicio y un
// `javascript:` en un href se ejecuta al hacer clic (React 18 solo lo advierte).
const safeHttpUrl = (url) => (typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null);

export default function DayPaymentModal({ open, onClose, driverId, driverName, date, tz, onChanged, isFreelancer = false }) {
  const { t } = useTranslation();
  // Autoría del extra: el backend la devuelve por extra (createdByName/createdAt); el
  // sello en la descripción sigue como respaldo (ver extraStamp).
  const { user, userData } = useAuth() ?? {};
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [extraAmount, setExtraAmount] = useState('');
  const [extraDesc, setExtraDesc] = useState('');
  const [reasons, setReasons] = useState({}); // packageId -> motivo
  // Fecha de pago (negocio, 'YYYY-MM-DD'). `dirty` protege lo que eligió el operador:
  // load() re-corre tras cada acción (extras, retenciones) y NO debe pisar la fecha.
  // Es un ref (no estado) para que cambiarla no re-dispare load() vía sus deps.
  const [payDate, setPayDate] = useState(todayYMD());
  const payDateDirty = useRef(false);

  useEffect(() => {
    setPayDate(todayYMD());
    payDateDirty.current = false;
  }, [open, date]);

  const load = useCallback(async () => {
    if (!open || !driverId || !date) return;
    setLoading(true);
    try {
      const res = await getDriverPayrollDay({ driverId, date, tz });
      setDetail(res?.data ?? null);
      if (!payDateDirty.current && res?.data?.paymentDate) setPayDate(res.data.paymentDate);
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo cargar el día'));
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [open, driverId, date, tz, t]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo completar la acción'));
    } finally {
      setBusy(false);
    }
  };

  const status = detail?.status ?? 'unpaid';
  const s = STATUS[status] ?? STATUS.unpaid;
  // Tipo con que billing pagó ESTE día: el del documento de su semana (o el vigente en
  // ella), no el del perfil actual. Un backend sin `docType` cae a `billingDocType`.
  const docType = detail?.docType ?? detail?.billingDocType;

  // Desglose de la tarifa de un paquete (responde "a qué tarifa se paga"): origen de la
  // base (comuna con regla propia vs. monto por defecto) y, si aplica, el recargo por
  // sobredimensionado (monto = base × multiplicador + fijo). Un XL con ×1 y +0 no recarga.
  const rateBreakdown = (p) => {
    if (!p?.priced) return t('sin tarifa');
    // Origen de la base: comuna con regla propia vs. monto por defecto. En pick & go
    // (entrega directa) tarifado por defecto —el operador desactivó el ajuste por comuna—
    // lo dejamos explícito para que se entienda por qué no tomó la tarifa de su comuna.
    const pickAndGo = p.deliveryMode === 'pick_and_go';
    const source =
      pickAndGo && !p.zonePriced
        ? t('por defecto (pick & go)')
        : p.zonePriced
          ? t('comuna propia')
          : t('por defecto');
    const ov = p.oversized;
    if (!ov) return source;
    const mult = ov.multiplier ?? 1;
    const add = ov.additionalCost ?? 0;
    if (mult === 1 && add === 0) {
      return `${source} · ${ov.category} ${t('(sin recargo)')}`;
    }
    const parts = [`${t('base')} ${formatCurrency(p.base)}`];
    if (mult !== 1) parts.push(`×${mult}`);
    if (add !== 0) parts.push(`+${formatCurrency(add)}`);
    return `${source} · ${parts.join(' ')}`;
  };

  // Resumen de sobredimensionados del día por categoría (p. ej. "XL 3 · 2XL 1").
  const oversizedSummary = (byCat) =>
    ['XL', '2XL', '3XL']
      .filter((c) => (byCat?.[c] ?? 0) > 0)
      .map((c) => `${c} ${byCat[c]}`)
      .join(' · ');

  // Al marcar pagado viaja la fecha elegida (explícita = sobrescritura intencional en el
  // backend); al desmarcar no se manda fecha (el backend la limpia junto con paidAt).
  const togglePaid = () =>
    act(() =>
      markDriverDayPaid({
        driverId,
        date,
        paid: status === 'unpaid',
        ...(status === 'unpaid' ? { paymentDate: payDate || undefined } : {}),
        tz,
      }),
    );

  const addExtra = () => {
    const amt = Number(extraAmount);
    if (!amt || !extraDesc.trim()) {
      toast.error(t('Ingresa monto (≠0) y descripción'));
      return;
    }
    act(() =>
      addDriverDayExtra({
        driverId,
        date,
        amount: amt,
        description: stampExtraDescription(extraDesc, user, userData),
      }).then(() => {
        setExtraAmount('');
        setExtraDesc('');
      }),
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`${driverName || driverId} · ${date}`}
      footer={<Button variant="outline" onClick={onClose}>{t('Cerrar')}</Button>}
    >
      {loading || !detail ? (
        <div className="text-sm text-fg-muted">{t('Cargando…')}</div>
      ) : (
        <div className="flex flex-col gap-5">
          {/* Estado + totales */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <span className={`bill-badge bill-badge--${s.cls}`}>{t(s.label)}</span>
              {/* Vehículo del repartidor, si viene en el detalle del día. */}
              {detail.vehicleType && (
                <span className="bill-badge bill-badge--neutral">
                  {driverVehicleLabel(detail.vehicleType, t)}
                </span>
              )}
              {/* Documento tributario. `null` → aviso (queda fuera de la planilla banco). */}
              {docType && (
                <span className="bill-badge bill-badge--neutral">
                  {billingDocLabel(docType, t)}
                </span>
              )}
              {/* La alerta aplica solo a no-freelancers (a los freelancers no se les paga). */}
              {docType === null && !isFreelancer && (
                <span
                  className="bill-badge bill-badge--warning"
                  title={t('Sin documento tributario: queda fuera de la planilla banco hasta que el repartidor elija factura o boleta.')}
                >
                  {t('Sin documento')}
                </span>
              )}
              <span className="text-sm text-fg-muted">
                {t('Total a cobrar')}: <span className="bill-td-price">{formatCurrency(detail.totalPayable)}</span>
              </span>
              {detail.oversizedCount > 0 && (
                <span className="text-xs text-fg-muted">
                  {t('Sobredim.')}: {oversizedSummary(detail.oversizedByCategory) || detail.oversizedCount}
                </span>
              )}
              {status === 'partial' && (
                <span className="text-xs text-status-warning">
                  {t('Retenido')}: {formatCurrency(detail.withheldAmount)} · {t('A pagar')}: {formatCurrency(detail.payableTotal)}
                </span>
              )}
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <div className="bill-field">
                <label className="bill-label" htmlFor="day-pay-date">{t('Fecha de pago')}</label>
                <Input
                  id="day-pay-date"
                  type="date"
                  size="sm"
                  value={payDate}
                  onChange={(e) => {
                    payDateDirty.current = true;
                    setPayDate(e.target.value);
                  }}
                />
              </div>
              {status !== 'unpaid' && (
                <>
                  {detail.paymentDate && (
                    <span className="text-xs text-fg-muted tabular-nums pb-2">
                      {t('Pagado el {{date}}', { date: dayLabel(detail.paymentDate) })}
                    </span>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy || !payDate}
                    onClick={() =>
                      act(() =>
                        setDriverPaymentDate({
                          paymentDate: payDate,
                          items: [{ driverId, dates: [date] }],
                        }),
                      )}
                  >
                    {t('Guardar fecha')}
                  </Button>
                </>
              )}
              <Button
                onClick={togglePaid}
                disabled={busy}
                variant={status === 'unpaid' ? 'primary' : 'outline'}
                iconLeft={status === 'unpaid' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              >
                {status === 'unpaid' ? t('Marcar pagado') : t('Marcar no pagado')}
              </Button>
            </div>
          </div>

          {/* Desglose por documento tributario: cómo el documento afecta el pago del día.
              boleta → monto del documento menos retención; factura → monto directo. */}
          {detail.docTotals && (
            <div className="text-xs text-fg-muted -mt-3">
              {docType === 'boleta'
                ? t('Boleta por {{doc}} · retención {{pct}}% → paga {{pay}}', {
                    doc: formatCurrency(detail.docTotals.documentAmount),
                    pct: Number(((detail.docTotals.retentionRate || 0) * 100).toFixed(2)),
                    pay: formatCurrency(detail.docTotals.payAmount),
                  })
                : t('Factura por {{doc}}', {
                    doc: formatCurrency(detail.docTotals.documentAmount),
                  })}
            </div>
          )}

          {/* Extras */}
          <div>
            <label className="text-sm font-medium text-fg">{t('Extras (bono / descuento)')}</label>
            <div className="flex flex-col gap-2 mt-2">
              {(detail.extras ?? []).map((e) => (
                <div key={e.id} className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-fg truncate">{e.description}</div>
                    {extraAuditLabel(e, t) && (
                      <div className="text-xs text-fg-muted truncate">{extraAuditLabel(e, t)}</div>
                    )}
                  </div>
                  <span className={`text-sm font-semibold ${e.amount < 0 ? 'text-status-error' : 'text-status-success'}`}>
                    {e.amount < 0 ? '' : '+'}{formatCurrency(e.amount)}
                  </span>
                  <button
                    type="button"
                    className="h-8 px-2 text-fg-muted hover:text-status-error"
                    disabled={busy}
                    onClick={() => act(() => removeDriverDayExtra({ driverId, date, extraId: e.id }))}
                    aria-label={t('Quitar')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <div className="flex items-end gap-2 flex-wrap">
                <div style={{ maxWidth: 140 }}>
                  <Input
                    type="number"
                    value={extraAmount}
                    onChange={(e) => setExtraAmount(e.target.value)}
                    placeholder={t('Monto (± )')}
                    size="sm"
                  />
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <Input
                    value={extraDesc}
                    onChange={(e) => setExtraDesc(e.target.value)}
                    placeholder={t('Descripción (ej. bono puntualidad)')}
                    size="sm"
                  />
                </div>
                <Button variant="outline" onClick={addExtra} disabled={busy} iconLeft={<Plus className="w-4 h-4" />}>
                  {t('Agregar')}
                </Button>
              </div>
            </div>
          </div>

          {/* Retiros realizados del día. `pickups` null o ausente = el día no paga retiros
              (config sin la opción o billing sin desplegar): no se muestra nada. Solo
              lectura: los retiros no se retienen. */}
          {detail.pickups && (
            <div>
              <h3 className="text-sm font-medium text-fg m-0">{t('Retiros del día')}</h3>
              {detail.pickups.status === 'unavailable' ? (
                <p role="alert" className="flex items-start gap-2 mt-2 mb-0 text-xs text-status-warning">
                  <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden />
                  {t('No se pudieron cargar los retiros realizados de este día: el total no los incluye. Reintenta en unos minutos.')}
                </p>
              ) : (detail.pickups.items ?? []).length === 0 ? (
                <p className="mt-2 mb-0 text-xs text-fg-muted">{t('Sin retiros realizados este día.')}</p>
              ) : (
                <div className="flex flex-col gap-2 mt-2 max-h-60 overflow-auto">
                  {detail.pickups.items.map((pk) => {
                    const time = timeLabel(pk.effectiveAt, tz);
                    const pdfHref = safeHttpUrl(pk.pdfUrl);
                    const remito =
                      pk.correlative != null ? t('Remito #{{n}}', { n: pk.correlative }) : t('Remito');
                    return (
                      <div
                        key={pk.pickupId ?? pk.collectId}
                        className="flex items-center gap-2 flex-wrap border-t border-border/60 pt-2"
                      >
                        <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                          <span className="text-sm text-fg truncate">{pk.clientName || '—'}</span>
                          <span className="flex items-center gap-2 flex-wrap text-[11px] text-fg-subtle">
                            {time && <span className="tabular-nums">{time}</span>}
                            {pdfHref ? (
                              <a
                                href={pdfHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={t('Ver el PDF')}
                                className="text-brand-700 hover:underline"
                              >
                                {remito}
                              </a>
                            ) : (
                              pk.correlative != null && <span>{remito}</span>
                            )}
                            {(pk.executedByName || pk.executedBy) && (
                              <span>
                                {t('Hecho por {{name}}', {
                                  name: pk.executedByName || t('otro repartidor de su flota'),
                                })}
                              </span>
                            )}
                          </span>
                        </div>
                        {/* amount null = la parada no tiene monto y el retiro suma 0. Si
                            igual viene `priced`, el monto existe pero lo cobra el líder de
                            la flota: rotularlo "Sin tarifa" sería falso. */}
                        {pk.amount != null ? (
                          <span className="bill-td-price text-sm">{formatCurrency(pk.amount)}</span>
                        ) : pk.priced ? (
                          <span className="text-sm text-fg-muted">—</span>
                        ) : (
                          <span className="text-xs text-status-warning">{t('Sin tarifa')}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Paquetes: retener / liberar */}
          <div>
            <label className="text-sm font-medium text-fg">{t('Entregas del día')}</label>
            <p className="text-xs text-fg-muted mb-2">
              {t('Retén un paquete (con motivo) para dejarlo sin pagar; el día queda parcial.')}
            </p>
            <div className="flex flex-col gap-2 max-h-72 overflow-auto">
              {(detail.packages ?? []).filter((p) => p.priced).map((p) => (
                <div
                  key={String(p.packageId)}
                  className="flex items-center gap-2 flex-wrap border-t border-border/60 pt-2"
                >
                  <span className="text-sm text-fg" style={{ minWidth: 90 }}>#{p.followingNumber}</span>
                  <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-xs text-fg-muted truncate">{p.municipalityName || '—'}</span>
                      {p.deliveryMode === 'pick_and_go' && (
                        <span className="bill-badge bill-badge--neutral bill-badge--sm shrink-0">
                          {t('Pick & go')}
                        </span>
                      )}
                      {p.oversized && (
                        <span className="bill-badge bill-badge--warning bill-badge--sm shrink-0">
                          {p.oversized.category}
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] text-fg-subtle">{rateBreakdown(p)}</span>
                  </div>
                  <span className="bill-td-price text-sm">{formatCurrency(p.unit)}</span>
                  {p.withheld ? (
                    <>
                      <span className="text-xs text-status-error truncate" style={{ maxWidth: 180 }}>
                        {t('Retenido')}: {p.withheldReason}
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        iconLeft={<Unlock className="w-4 h-4" />}
                        onClick={() => act(() => releaseDriverDayPackage({ driverId, date, packageId: String(p.packageId) }))}
                      >
                        {t('Liberar')}
                      </Button>
                    </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div style={{ width: 170 }}>
                        <Input
                          value={reasons[String(p.packageId)] ?? ''}
                          onChange={(e) => setReasons((r) => ({ ...r, [String(p.packageId)]: e.target.value }))}
                          placeholder={t('Motivo (ej. reclamo)')}
                          size="sm"
                        />
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busy}
                        iconLeft={<Lock className="w-4 h-4" />}
                        onClick={() => {
                          const reason = (reasons[String(p.packageId)] ?? '').trim();
                          if (!reason) {
                            toast.error(t('Indica un motivo para retener'));
                            return;
                          }
                          act(() => withholdDriverDayPackage({ driverId, date, packageId: String(p.packageId), reason }));
                        }}
                      >
                        {t('Retener')}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
